import React, { useState, useEffect } from 'react';
import { Box, DialogActions, Grid, TextField, Typography, Button, FormControl, InputLabel, Select, MenuItem, LinearProgress, Autocomplete, Snackbar, Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getData, postData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';

const CreateProject = ({ open, onClose, onSuccess }) => {
    const today = new Date();
    const [newProject, setNewProject] = useState({
        name: '',
        client: '',
        projectManager: null,
        teamLead: null,
        startDate: null,
        endDate: null,
        status: 'Todo',
        description: '',
        priority: 'Medium',
        progress: 0,
        manager_id: null,
        team_leader_id: null
    });

    const [errors, setErrors] = useState({});
    const [teamLeadOptions, setTeamLeadOptions] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const organizationId = userData?.organization_id;
    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const validate = () => {
        let tempErrors = {};
        if (!newProject.name.trim()) tempErrors.name = "Project Name is required";
        if (!newProject.client.trim()) tempErrors.client = "Client name is required";
        if (!newProject.manager_id) tempErrors.manager = "Please select a Project Manager";
        if (!newProject.team_leader_id) tempErrors.teamLead = "Please select a Team Lead";
        if (!newProject.startDate) tempErrors.startDate = "Start date is required";
        if (!newProject.endDate) tempErrors.endDate = "End date is required";

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    useEffect(() => {
        const fetchData = async () => {
            const userDataRaw = localStorage.getItem('userData');
            const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
            const organizationId = userData?.organization_id;

            if (!organizationId) return;

            try {
                const [teamLeadRes, managerRes] = await Promise.all([
                    getData(API_ENDPOINTS.fetchTeamLeadsOrManagers(organizationId, 'is_team_lead')),
                    getData(API_ENDPOINTS.fetchTeamLeadsOrManagers(organizationId, 'is_manager'))
                ]);

                if (teamLeadRes?.status === "SUCCESS") {
                    setTeamLeadOptions(teamLeadRes.message?.users?.map(u => ({
                        id: u.id,
                        name: `${u.first_name || ''} ${u.last_name || ''}`.trim()
                    })) || []);
                }

                if (managerRes?.status === "SUCCESS") {
                    setManagerOptions(managerRes.message?.users?.map(u => ({
                        id: u.id,
                        name: `${u.first_name || ''} ${u.last_name || ''}`.trim()
                    })) || []);
                }
            } catch (error) {
               return null;
            }
        };
        fetchData();
    }, []);

    const handleCreateProject = async () => {
        if (!validate()) {
            setSnackbar({ open: true, message: 'Please correct the highlighted errors.', severity: 'error' });
            return;
        }

        setIsLoading(true);
        try {
            const payload = {
                organization_id: organizationId,
                project_title: newProject.name,
                priority: newProject.priority,
                start_date: newProject.startDate.toISOString(),
                end_date: newProject.endDate.toISOString(),
                description: newProject.description,
                clients: [{ first_name: newProject.client, last_name: '' }],
                status: newProject.status,
                progress: newProject.progress || 0,
                manager_id: newProject.manager_id,
                team_leader_id: newProject.team_leader_id,
            };

            const response = await postData(API_ENDPOINTS.createNewProject, payload);
            if (response?.status === 'SUCCESS') {
                setSnackbar({ open: true, message: 'Project created successfully!', severity: 'success' });
                setTimeout(() => {
                    onSuccess();
                    handleCancelButton();
                }, 500);
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Error creating project.', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelButton = () => {
        setNewProject({
            name: '', client: '', projectManager: null, teamLead: null,
            startDate: null, endDate: null, status: 'Todo', description: '',
            priority: 'Medium', progress: 0, manager_id: null, team_leader_id: null
        });
        setErrors({});
        onClose();
    };

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        label="Project Name"
                        fullWidth
                        required
                        size="small"
                        error={!!errors.name}
                        helperText={errors.name}
                        value={newProject.name}
                        onChange={(e) => {
                            const val = e.target.value;
                            const capitalizedValue = val.charAt(0).toUpperCase() + val.slice(1);
                            setNewProject({ ...newProject, name: capitalizedValue });
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        label="Client"
                        fullWidth
                        required
                        size="small"
                        error={!!errors.client}
                        helperText={errors.client}
                        value={newProject.client}
                        onChange={(e) => {
                            const val = e.target.value;
                            const capitalizedValue = val.charAt(0).toUpperCase() + val.slice(1);
                            setNewProject({ ...newProject, client: capitalizedValue })
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                        options={managerOptions}
                        getOptionLabel={(option) => option.name}
                        value={newProject.projectManager}
                        onChange={(e, val) => setNewProject({ ...newProject, projectManager: val, manager_id: val?.id || null })}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Project Manager"
                                required
                                size="small"
                                error={!!errors.manager}
                                helperText={errors.manager}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                        options={teamLeadOptions}
                        getOptionLabel={(option) => option.name}
                        value={newProject.teamLead}
                        onChange={(e, val) => setNewProject({ ...newProject, teamLead: val, team_leader_id: val?.id || null })}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Team Lead"
                                required
                                size="small"
                                error={!!errors.teamLead}
                                helperText={errors.teamLead}
                            />
                        )}
                    />
                </Grid>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <DatePicker
                            label="Start Date"
                            sx={{ width: '100%' }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    required: true,
                                    error: !!errors.startDate,
                                    helperText: errors.startDate
                                }
                            }}
                            minDate={today}
                            value={newProject.startDate}
                            onChange={(date) => setNewProject({ ...newProject, startDate: date })}
                            format='dd/MM/yyyy'
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <DatePicker
                            label="End Date"
                            sx={{ width: '100%' }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    required: true,
                                    error: !!errors.endDate,
                                    helperText: errors.endDate
                                }
                            }}
                            minDate={newProject.startDate || today}
                            value={newProject.endDate}
                            onChange={(date) => setNewProject({ ...newProject, endDate: date })}
                            format='dd/MM/yyyy'
                        />
                    </Grid>
                </LocalizationProvider>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Priority</InputLabel>
                        <Select
                            value={newProject.priority}
                            label="Priority"
                            onChange={(e) => setNewProject({ ...newProject, priority: e.target.value })}
                        >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                        value={newProject.description}
                        onChange={(e) => {
                            const val = e.target.value;
                            const capitalizedValue = val.charAt(0).toUpperCase() + val.slice(1);
                            setNewProject({ ...newProject, description: capitalizedValue });
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ p: 2, borderRadius: '12px', background: '#f8f9ff', border: '1px solid #e0e7ff' }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ color: '#3b82f6', fontWeight: 600 }}>
                            Project Progress ({newProject.progress}%)
                        </Typography>
                        <LinearProgress variant="determinate" value={Number(newProject.progress)} sx={{ height: 10, borderRadius: 5, my: 2 }} />
                        <TextField
                            type="number"
                            label="Progress %"
                            size="small"
                            value={newProject.progress}
                            onFocus={(e) => {
                                if (newProject.progress === 0) {
                                    setNewProject({ ...newProject, progress: '' });
                                }
                            }}
                            onBlur={(e) => {
                                if (newProject.progress === '') {
                                    setNewProject({ ...newProject, progress: 0 });
                                }
                            }}
                            onChange={(e) => {
                                const val = e.target.value;
                                const parsedVal = val === '' ? '' : Math.min(100, Math.max(0, parseInt(val) || 0));
                                setNewProject({ ...newProject, progress: parsedVal });
                            }}
                            fullWidth
                        />
                    </Box>
                </Grid>
            </Grid>

            <DialogActions>
                <Button onClick={handleCancelButton} variant="outlined" disabled={isLoading} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button onClick={handleCreateProject} variant="contained" disabled={isLoading} sx={{ textTransform: 'none' }}>
                    {isLoading ? 'Processing...' : 'Create Project'}
                </Button>
            </DialogActions>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}> {snackbar.message} </Alert>
            </Snackbar>
        </>
    );
};

export default CreateProject;